import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom, Subject } from "rxjs";
import { AppointmentBackendRow, AppointmentDetailResponse, AppointmentsResponse } from "allai-admin-shared";
import { environment } from '../../../environments/environment.js';
import { SnackbarService } from '../snackbar.service.js';
import { SessionService } from "../session.service";
import { AppointmentsFilterService } from './appointments-filter.service.js';
import { PageLoaderService } from "../page-loader.service";
import { ActivatedRouteSnapshot } from '@angular/router';
import { SurveyInstance } from "@allaihealth/survey";

/** Service that loads and caches all appointments in the appointment list page */
@Injectable({
	providedIn: 'root'
})
export class AppointmentsService {
	/** Loaded appointments by id */
	private _appointmentList:AppointmentBackendRow[];
	private _total:number = 100;
	private _appointmentDetail:AppointmentDetailResponse;

	/** Subject for a new appointments list, usually from a filter change */
	private newAppointmentsSubject = new Subject<void>();

	constructor(private http:HttpClient,
	            private sessionService:SessionService,
	            private appointmentsFilterService:AppointmentsFilterService,
	            private pageLoaderService:PageLoaderService,
	            private snackbar:SnackbarService) {
		//clear things out if the user logs out
		this.sessionService.onLogout.subscribe(() => {
			this.clear();
		});

		//listen to filter changes
		this.appointmentsFilterService.onNewFilter.subscribe(() => {
			this.pageLoaderService.showAndListen(this.resolveAppointments());
		});
	}

	/** Resolver to get all appointments for the list if not already loaded */
	static resolveAppointments():Promise<AppointmentBackendRow[]> {
		return inject(AppointmentsService).resolveAppointments();
	}

	/** Resolver to get all appointments for the list if not already loaded */
	async resolveAppointments():Promise<AppointmentBackendRow[]> {
		try {
			const params:HttpParams = this.appointmentsFilterService.params;

			//load from server
			const response = await firstValueFrom(this.http.get<AppointmentsResponse>(`${environment.apiUrl}/appointments`, {params}));

			this._appointmentList = response.appointments;
			this._total = response.total;

			//dispatch that there was a change
			this.newAppointmentsSubject.next();
		} catch(e) {
			//clear filter on error, just in case
			this.appointmentsFilterService.clear();

			console.error("Failed to load appointments from server", e);
			this.snackbar.error("Failed to download appointments");
		}

		return this._appointmentList;
	}

	/** Resolver to get the appointment that is clicked on */
	static resolveAppointmentDetail = async(route:ActivatedRouteSnapshot) => {
		return inject(AppointmentsService).resolveAppointmentDetail(route.params["id"]);
	}

	/** Resolver to get all appointments for the list if not already loaded */
	async resolveAppointmentDetail(appointmentId:string) {
		if(appointmentId != this._appointmentDetail?.appointment.id) {
			try {
				this._appointmentDetail = await firstValueFrom(this.http.get<AppointmentDetailResponse>(
					`${environment.apiUrl}/appointments/${appointmentId}`));
			} catch(e) {
				console.error(`Failed to load appointment ${appointmentId} from server`, e);
				this.snackbar.error(`Failed to load appointment ${appointmentId} from server`);
				return false;
			}
		}

		return this._appointmentDetail;
	}

	/** Clear out loaded appointments and force a reload */
	clear() {
		this._appointmentList = undefined;
		this._total = 100;
	}

	async saveAnswers(answers:SurveyInstance):Promise<void> {
		const surveyLinkId = this._appointmentDetail.appointment.id_patient_survey_link;
		await firstValueFrom(this.http.put<void>(`${environment.apiUrl}/surveys/${surveyLinkId}/answers`, answers));
		this._appointmentDetail.answers = answers;
	}

	/** Get the sorted appointments */
	get appointmentList() {
		return this._appointmentList;
	}

	/** Get the appointment detail */
	get appointmentDetail() {
		return this._appointmentDetail;
	}

	/** Dispatched for a new appointments list, usually from a filter change */
	get onNewAppointments() {
		return this.newAppointmentsSubject.asObservable();
	}

	get total() {
		return this._total;
	}

	async completeSurvey() {
		const surveyLinkId = this._appointmentDetail.appointment.id_patient_survey_link;
		await firstValueFrom(this.http.post<void>(`${environment.apiUrl}/surveys/${surveyLinkId}/complete`,{}));
	}
}
